import { IBusinessStatus, BusinessStatus, BusinessSubStatus } from '../../../../api/define';

type IhandlerItem = {
    text: string;
    btn: {
        text: string;
        status?: number;
        /** 按钮是否置灰 */
        disabled?: boolean;
        /** 跳转链接 */
        jumpUrl?: string;
    };
};

type Iresult = {
    headerText: string;
    headerTag: string;
    handlerItems: IhandlerItem[];
};

/**
 * 获取营业状态数据
 * @param statusInfo
 * @return { headerText, headerTag, handlerItems } {门店状态文案, 门店状态tag, 门店状态操作栏}
 *
 * 门店状态操作栏: { text, btn: {status, text}}
 */
const getStatusText = (statusInfo: IBusinessStatus): Iresult => {
    const statusDescInfo = statusInfo.statusDescInfo || '';
    const wmPoiStatusDesc = statusInfo.wmPoiStatusDesc || '';
    const {
        status,
        subStatus,
        restType,
        restDesc,
        restTime,
        statusRestoreMaxTime,
        valid,
        operationStatus,
        restCode,
    } = statusInfo;
    const result: Iresult = {
        /** 门店状态文案 */
        headerText: '',
        /** 门店状态tag */
        headerTag: '',
        /** 门店状态操作栏 */
        handlerItems: [],
    };

    if (valid === 2 || valid === 3) {
        // 门店上线中
        result.headerText = '本店上线中，请继续完成开门营业任务';
        result.headerTag = '门店上线中';
        result.handlerItems = [
            {
                text: '完成开门营业任务后，系统会根据营业时间自动切换营业中、已打烊状态。',
                btn: {
                    jumpUrl: '/new_fe/operation_gw#/welcome',
                    text: '开门营业',
                },
            },
        ];
    } else if (statusDescInfo) {
        result.headerText = statusDescInfo;
        result.headerTag = wmPoiStatusDesc;

        if (status === BusinessStatus.OPEN) {
            /** 非置休 */
            /** 更改营业状态 */
            const handlerItem = {
                text: '停业后，门店将不再接收新订单，不再会自动恢复营业。',
                btn: {
                    status: BusinessStatus.REST,
                    text: '停止营业',
                },
            };
            result.handlerItems = [handlerItem];

            if (
                subStatus === BusinessSubStatus.RESERVE ||
                subStatus === BusinessSubStatus.WILLOPEN
            ) {
                /** 更改营业时间 */
                const timeHandlerItem = {
                    text: '如您期望调整当前营业状态，可更改营业时间、预订设置',
                    btn: {
                        jumpUrl: '/v2/shop/manage/shopInfo',
                        text: '营业调整',
                    },
                };
                result.handlerItems = [handlerItem, timeHandlerItem];
            }
        }

        /** 置休 */
        if (status === BusinessStatus.REST) {
            /** 非强制置休 */
            if (restType === undefined || restType === 0 || restType === 2) {
                /** 更改营业状态 */
                const handlerItem = {
                    text: '恢复营业后，系统会根据营业时间自动切换营业中、已打烊状态。',
                    btn: {
                        status: BusinessStatus.OPEN,
                        text: '恢复营业',
                    },
                };
                result.handlerItems = [handlerItem];
            }

            /** 强制置休 */
            if (restType === 1 || restType === 3) {
                /** 更改营业状态 */
                if (restCode === 1896) {
                    const handlerItem = {
                        text:
                            '本店停业中。停业原因：门店所在品牌《平台商户入驻服务框架协议》等合同协议到期且未续约，门店暂不能恢复营业。门店可在品牌与平台完成续签后恢复营业。您可通过品牌方联系品牌业务经理反馈意见和获得更多解释说明。',
                        btn: {
                            status: BusinessStatus.OPEN,
                            disabled: true,
                            text: '恢复营业',
                        },
                    };
                    result.handlerItems = [handlerItem];
                } else {
                    const handlerItem = {
                        text: '不可提前恢复营业，请耐心等待自动恢复营业。',
                        btn: {
                            status: BusinessStatus.OPEN,
                            disabled: true,
                            text: '恢复营业',
                        },
                    };
                    result.handlerItems = [handlerItem];
                }
            }

            /** 歇业保护生效中 */
            if (operationStatus === 1) {
                /** 更改营业状态 */
                const handlerItem = {
                    text:
                        '【您正处于歇业保护中，确认恢复营业会自动撤销当前正在生效的歇业保护】恢复营业后，系统会根据营业时间自动切换营业中、已打烊状态',
                    btn: {
                        status: BusinessStatus.OPEN,
                        text: '恢复营业',
                    },
                };
                result.handlerItems = [handlerItem];
            }
        }
    } else {
        //statusDescInfo不存在时的各状态文字设置
        /** 非置休 */
        if (status === BusinessStatus.OPEN) {
            /** 营业中 */
            if (subStatus === BusinessSubStatus.OPEN) {
                result.headerText = `当前餐厅处于设置的营业时间内，正常接受新订单`;
                result.headerTag = '营业中';

                /** 营业状态调整 */
                const handlerItem = {
                    text:
                        '适用于较长时间停止提供服务，不接受任何订单，手动恢复营业后可正常接受订单',
                    btn: {
                        status: BusinessStatus.REST,
                        text: '停止营业',
                    },
                };
                result.handlerItems = [handlerItem];
            }

            /** 仅接受预定 */
            /* jscpd:ignore-start */
            if (subStatus === BusinessSubStatus.RESERVE) {
                result.headerText = `当前餐厅处于设置的营业时间外，且设置了接受预订单，新订单会以预订单的形式提示`;
                result.headerTag = '仅接受预订';

                /** 营业状态调整 */
                const handlerItem = {
                    text:
                        '适用于较长时间停止提供服务，不接受任何订单，手动恢复营业后可正常接受订单',
                    btn: {
                        status: BusinessStatus.REST,
                        text: '停止营业',
                    },
                };

                /** 营业时间调整 */
                const timeHandlerItem = {
                    text: '如您期望调整当前营业状态，可更改营业时间、预订设置',
                    btn: {
                        jumpUrl: '/v2/shop/manage/shopInfo',
                        text: '营业调整',
                    },
                };

                result.handlerItems = [handlerItem, timeHandlerItem];
            }
            /* jscpd:ignore-end */

            /** 待开始营业 */
            /* jscpd:ignore-start */
            if (subStatus === BusinessSubStatus.WILLOPEN) {
                result.headerText = `当前餐厅处于设置的营业时间外，网站上会显示餐厅休息中`;
                result.headerTag = '待开始营业';

                /** 营业状态调整 */
                const handlerItem = {
                    text:
                        '适用于较长时间停止提供服务，不接受任何订单，手动恢复营业后可正常接受订单',
                    btn: {
                        status: BusinessStatus.REST,
                        text: '停止营业',
                    },
                };

                /** 营业时间调整 */
                const timeHandlerItem = {
                    text: '如您期望调整当前营业状态，可更改营业时间、预订设置',
                    btn: {
                        jumpUrl: '/v2/shop/manage/shopInfo',
                        text: '营业调整',
                    },
                };

                result.handlerItems = [handlerItem, timeHandlerItem];
            }
            /* jscpd:ignore-end */
        }

        /** 置休 */
        if (status === BusinessStatus.REST) {
            /** 非强制置休 */
            if (restType === undefined || restType === 0 || restType === 2) {
                result.headerText = `当前餐厅处于较长时间停止提供服务，不接受任何订单，手动恢复营业后可正常接受订单`;
                result.headerTag = '停止营业中';

                /** 营业状态调整 */
                const handlerItem = {
                    text: `系统将根据餐厅的营业时间、预订设置自动恢复营业`,
                    btn: {
                        status: BusinessStatus.OPEN,
                        text: '恢复营业',
                    },
                };

                result.handlerItems = [handlerItem];
            }

            /** 强制置休 */
            if (restType === 1 || restType === 3) {
                result.headerText = `您的门店因为${restDesc}被设置为休息${restTime}，请联系您的美团业务经理，谢谢！`;
                result.headerTag = '停止营业中';

                /** 营业状态调整 */
                const handlerItem = {
                    text: `系统将于${statusRestoreMaxTime}自动恢复`,
                    btn: {
                        status: BusinessStatus.OPEN,
                        text: '恢复营业',
                        disabled: true,
                    },
                };

                result.handlerItems = [handlerItem];
            }
        }
    }

    return result;
};

export default getStatusText;
